import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Trophy, ChevronLeft, Medal, Flame, TrendingUp, Users,
  Crown, Award, Star
} from "lucide-react";

// Mini mandala for badges in leaderboard
const MiniBadge = ({ color, size = 32 }) => {
  const colors = {
    bronze: "#CD7F32",
    silver: "#C0C0C0",
    gold: "#FFD700"
  };
  
  return (
    <svg width={size} height={size} viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="none" stroke={colors[color]} strokeWidth="1.5" opacity="0.5" />
      <circle cx="16" cy="16" r="10" fill={colors[color]} opacity="0.3" />
      <circle cx="16" cy="16" r="6" fill={colors[color]} />
    </svg>
  );
};

const getRankIcon = (rank) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="text-gray-400 font-medium w-5 text-center">{rank}</span>;
};

const getRankStyle = (rank) => {
  if (rank === 1) return "from-yellow-900/40 to-yellow-800/20 border-yellow-500/40";
  if (rank === 2) return "from-gray-700/40 to-gray-800/20 border-gray-400/40";
  if (rank === 3) return "from-amber-900/40 to-amber-800/20 border-amber-600/40";
  return "from-[#1a1a3a]/60 to-[#0d0d2b]/40 border-white/10";
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [totalPractitioners, setTotalPractitioners] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all_time");

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/maya-manthan/leaderboard`, {
        params: { period, limit: 25 }
      });
      setLeaderboard(response.data.leaderboard || []);
      setUserRank(response.data.user_rank);
      setTotalPractitioners(response.data.total_practitioners || 0);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="leaderboard-page">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />
      
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => navigate('/maya-manthan')}
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-serif text-3xl text-white flex items-center gap-3">
              <Trophy className="w-8 h-8 text-amber-400" />
              Leaderboard
            </h1>
            <p className="text-gray-400 text-sm">Top practitioners by longest streak</p>
          </div>
        </div>

        {/* Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-900/30 to-purple-900/30 border border-amber-500/20 rounded-xl p-4 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-white font-medium">{totalPractitioners} Practitioners</p>
                <p className="text-gray-400 text-xs">on the spiritual path</p>
              </div>
            </div>
            {userRank && (
              <div className="text-right">
                <p className="text-amber-400 text-sm">Your Rank</p>
                <p className="text-white text-2xl font-bold">#{userRank}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Period Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "all_time", label: "All Time" },
            { id: "this_month", label: "This Month" },
            { id: "this_week", label: "This Week" }
          ].map((p) => (
            <Button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              variant={period === p.id ? "default" : "outline"}
              className={period === p.id 
                ? "bg-amber-500 hover:bg-amber-600 text-black" 
                : "border-white/20 text-white hover:bg-white/10"
              }
              size="sm"
            >
              {p.label}
            </Button>
          ))}
        </div>

        {/* Leaderboard List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leaderboard.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">No practitioners yet</h3>
            <p className="text-gray-400 text-sm mb-4">Be the first to start your streak!</p>
            <Button
              onClick={() => navigate('/maya-manthan')}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Start Practicing
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((entry, idx) => (
              <motion.div
                key={entry.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`relative bg-gradient-to-r ${getRankStyle(entry.rank)} backdrop-blur-xl border rounded-xl p-4 ${
                  entry.is_current_user ? 'ring-2 ring-amber-500/50' : ''
                }`}
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className="w-8 h-8 flex items-center justify-center">
                    {getRankIcon(entry.rank)}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium truncate ${entry.is_current_user ? 'text-amber-400' : 'text-white'}`}>
                        {entry.name}
                        {entry.is_current_user && <span className="text-xs ml-2">(You)</span>}
                      </p>
                      {entry.badge && (
                        <MiniBadge color={entry.badge.color} size={24} />
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <Flame className="w-3 h-3" />
                        {entry.current_streak} current
                      </span>
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {entry.consistency_score}% consistency
                      </span>
                    </div>
                  </div>
                  
                  {/* Streak */}
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">{entry.longest_streak}</p>
                    <p className="text-gray-400 text-xs">days</p>
                  </div>
                </div>
                
                {/* Current user highlight */}
                {entry.is_current_user && (
                  <div className="absolute -top-1 -right-1">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* User not in top list */}
        {userRank && userRank > 25 && !leaderboard.find(e => e.is_current_user) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-4 bg-gradient-to-r from-amber-900/30 to-amber-800/20 border border-amber-500/30 rounded-xl p-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 flex items-center justify-center">
                <span className="text-amber-400 font-bold">#{userRank}</span>
              </div>
              <div className="flex-1">
                <p className="text-amber-400 font-medium">Your Position</p>
                <p className="text-gray-400 text-xs">Keep practicing to climb the leaderboard!</p>
              </div>
              <Button
                onClick={() => navigate('/maya-manthan')}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Practice Now
              </Button>
            </div>
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 flex justify-center gap-4"
        >
          <Button
            onClick={() => navigate('/maya-manthan/badges')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Award className="w-4 h-4 mr-2" />
            View Badges
          </Button>
          <Button
            onClick={() => navigate('/maya-manthan')}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Flame className="w-4 h-4 mr-2" />
            Continue Practice
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
